import { NoteVersionPreviewComponent } from './note-version-preview.component';
import {
  afterNextRender,
  ElementRef,
  Injector,
  viewChild,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { type Subscription, switchMap, take } from 'rxjs';
import { NotesService } from '../services/notes.service';
import { type Note } from '../types/note';
import { noteVersionError, NoteVersionsClient, type NoteVersion } from './note-versions.client';

export { noteVersionError } from './note-versions.client';

@Component({
  selector: 'app-note-version-history',
  standalone: true,
  imports: [DateFormatPipe, NoteVersionPreviewComponent],
  providers: [NoteVersionsClient],
  templateUrl: './note-version-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteVersionHistoryComponent {
  public readonly note = input.required<Note>();
  public readonly canEdit = input(false);
  public readonly saving = input(false);
  public readonly prepareRestore = input<(continueRestore: () => void) => boolean>(() => true);
  public readonly refreshed = output<Note>();
  public readonly restoring = output<boolean>();
  private readonly client = inject(NoteVersionsClient);
  private readonly injector = inject(Injector);
  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('restoreCancel');
  private readonly focusCancel = effect(() => {
    const button = this.cancelButton();
    if (!button) return;
    afterNextRender(
      () => {
        if (this.cancelButton() === button) button.nativeElement.focus();
      },
      { injector: this.injector },
    );
  });
  private readonly notes = inject(NotesService);
  private readonly space = inject(SpaceContextStore);
  protected readonly available = linkedSignal(() => {
    const note = this.note();
    return !!note.id && this.space.insideTeam() && note.accountId === this.space.contextId();
  });
  protected readonly page = linkedSignal(() => {
    this.note();
    this.space.contextId();
    return 0;
  });
  protected readonly rows = signal<readonly NoteVersion[]>([]);
  protected readonly current = signal<number | null>(null);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);
  protected readonly failure = signal<string | null>(null);
  protected readonly previewOpened = signal(false);
  protected readonly selected = signal<number | null>(null);
  protected readonly confirmation = signal<number | null>(null);
  protected readonly busy = signal(false);
  private readonly revision = signal(0);
  private restoreSubscription?: Subscription;
  private generation = 0;

  private readonly identity = effect(() => {
    this.note();
    this.space.contextId();
    this.space.insideTeam();
    untracked(() => {
      this.generation++;
      this.restoreSubscription?.unsubscribe();
      this.busy.set(false);
      this.restoring.emit(false);
      this.previewOpened.set(false);
      this.selected.set(null);
      this.confirmation.set(null);
    });
  });
  private readonly load = effect((onCleanup) => {
    const note = this.note();
    const available = this.available();
    const offset = this.page() * 25;
    this.revision();
    this.rows.set([]);
    this.total.set(0);
    this.current.set(null);
    this.failure.set(null);
    this.loading.set(available);
    if (!available) return;
    const subscription = this.client.list(note, offset).subscribe({
      next: (answer) => {
        this.rows.set(answer.items);
        this.current.set(answer.current);
        this.total.set(answer.total);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.failure.set(noteVersionError(error));
        this.loading.set(false);
      },
    });
    onCleanup(() => subscription.unsubscribe());
  });

  public constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.generation++;
      this.restoreSubscription?.unsubscribe();
    });
  }
  protected fields(row: NoteVersion): string {
    return row.fields
      .map((field) =>
        field === 'title' ? 'Title' : field === 'description' ? 'Description' : field === 'body' ? 'Body' : field,
      )
      .join(', ');
  }
  protected review(row: NoteVersion): string {
    return row.review === 'requested'
      ? 'Review requested'
      : row.review === 'approved'
        ? 'Approved'
        : 'Changes requested';
  }
  protected choose(version: number): void {
    if (this.busy()) return;
    this.previewOpened.set(this.selected() !== version || !this.previewOpened());
    this.selected.set(version);
    this.confirmation.set(null);
  }
  protected askRestore(version: number): void {
    if (!this.canEdit() || this.saving() || this.busy() || version === this.current()) return;
    if (this.selected() !== version) this.previewOpened.set(false);
    this.selected.set(version);
    this.confirmation.set(version);
    this.failure.set(null);
  }
  protected cancel(): void {
    if (!this.busy()) {
      this.confirmation.set(null);
      this.failure.set(null);
    }
  }
  protected confirmRestore(): void {
    const version = this.confirmation();
    if (
      version === null ||
      version === this.current() ||
      !this.available() ||
      !this.canEdit() ||
      this.saving() ||
      this.busy()
    )
      return;
    const note = this.note();
    const generation = this.generation;
    if (
      !this.prepareRestore()(() => {
        if (this.generation === generation && this.confirmation() === version) this.confirmRestore();
      })
    )
      return;
    this.busy.set(true);
    this.restoring.emit(true);
    this.failure.set(null);
    this.restoreSubscription = this.client
      .restore(note, version)
      .pipe(
        // Both restored:true and restored:false require the authoritative current note.
        switchMap(() => this.notes.getItem(encodeURIComponent(note.id), { tenantItems: [note] }).pipe(take(1))),
      )
      .subscribe({
        next: (fresh) => {
          if (this.generation !== generation || this.note() !== note || !this.available()) return;
          if (fresh.id !== note.id || fresh.accountId !== note.accountId) {
            this.failure.set('Note not found');
            this.busy.set(false);
            this.restoring.emit(false);
            return;
          }
          this.refreshed.emit(fresh);
          this.confirmation.set(null);
          this.selected.set(null);
          this.busy.set(false);
          this.restoring.emit(false);
          this.page.set(0);
          this.revision.update((value) => value + 1);
        },
        error: (error: unknown) => {
          if (this.generation !== generation) return;
          this.failure.set(noteVersionError(error));
          this.busy.set(false);
          this.restoring.emit(false);
        },
      });
  }
  protected retry(): void {
    if (!this.busy()) this.revision.update((value) => value + 1);
  }
  protected previous(): void {
    if (!this.busy()) {
      this.confirmation.set(null);
      this.selected.set(null);
      this.page.update((page) => Math.max(0, page - 1));
    }
  }
  protected next(): void {
    if (!this.busy() && (this.page() + 1) * 25 < this.total()) {
      this.confirmation.set(null);
      this.selected.set(null);
      this.page.update((page) => page + 1);
    }
  }
}
